/**
 * Shared quota display utilities for ExtractionQuota.
 * Used by ExtractionQuotaCard (PaymentsSettings) — single source of truth for
 * quota coloring, progress and date formatting so future additions stay consistent.
 */
import { Temporal } from '@insio/dates';

export interface QuotaInfo {
  used: number;
  limit: number | null;
  canExceedLimit: boolean;
  /** Absolute hard cap (base + overages). Null = no hard cap beyond base limit. */
  hardCap: number | null;
  windowStart: string;
  windowEnd: string;
  period: string;
}

/** `Account.extractionQuota` as the API returns it — every figure is null for a client managed by a master account. */
export interface ExtractionQuotaFields {
  used?: number | null;
  limit?: number | null;
  canExceedLimit?: boolean | null;
  hardCap?: number | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  period?: string | null;
}

/**
 * The account's own figures as QuotaInfo, or null when it has none — a managed client
 * gets only a status, and every numeric consumer must show nothing rather than zeros.
 */
export function toQuotaInfo(quota: ExtractionQuotaFields | null | undefined): QuotaInfo | null {
  if (
    !quota ||
    quota.used == null ||
    quota.canExceedLimit == null ||
    quota.windowStart == null ||
    quota.windowEnd == null ||
    quota.period == null
  ) {
    return null;
  }
  return {
    used: quota.used,
    limit: quota.limit ?? null,
    canExceedLimit: quota.canExceedLimit,
    hardCap: quota.hardCap ?? null,
    windowStart: quota.windowStart,
    windowEnd: quota.windowEnd,
    period: quota.period
  };
}

/** Usage as a 0-100 percentage of the base limit, or null for unlimited quotas. */
export function quotaPercent(quota: QuotaInfo): number | null {
  if (quota.limit === null || quota.limit === 0) return null;
  return Math.min((quota.used / quota.limit) * 100, 100);
}

/**
 * Tailwind text-color class for the quota value.
 * Intentionally uses the `-text` semantic tokens (success-text, warn-text, error-text) —
 * these are the dark/high-contrast variants designed for readable text on white backgrounds,
 * as opposed to the lighter `-bg` / `-border` tokens used for badge backgrounds.
 */
export function quotaTextColorClass(pct: number | null): string {
  if (pct === null) return 'text-foreground-muted';
  if (pct >= 80) return 'text-error-text';
  if (pct >= 50) return 'text-warn-text';
  return 'text-success-text';
}

/**
 * Tailwind background-color class for the progress fill.
 * Uses the `-border` tokens which sit between the very light `-bg` and the dark `-text` —
 * a good mid-weight fill colour that stays legible inside a progress bar track.
 */
export function quotaBarColorClass(pct: number | null): string {
  if (pct === null) return 'bg-border-base';
  if (pct >= 80) return 'bg-error-border';
  if (pct >= 50) return 'bg-warn-border';
  return 'bg-success-border';
}

/**
 * Format the quota label as "16 / 50" or "16 / ∞" for truly unlimited plans (no limit set).
 * Always shows usage capped at the plan limit (base plan usage only).
 * Plans that allow exceeding still display the actual limit — extra docs are tracked separately.
 */
export function formatQuotaLabel(quota: QuotaInfo): string {
  if (quota.limit === null) {
    return `${quota.used} / ∞`;
  }
  const displayUsed = Math.min(quota.used, quota.limit);
  return `${displayUsed} / ${quota.limit}`;
}

/** Number of extractions that exceed the monthly plan limit. Only non-zero when the plan allows exceeding (overage model). */
export function quotaExcessCount(quota: QuotaInfo): number {
  if (!quota.canExceedLimit) return 0;
  if (quota.limit === null || quota.limit === 0) return 0;
  return Math.max(quota.used - quota.limit, 0);
}

/** "1 June 2026" — the start of the next billing cycle (= windowEnd). */
export function formatQuotaResetDate(quota: QuotaInfo, locale: string): string {
  const windowEndDate = Temporal.Instant.from(quota.windowEnd)
    .toZonedDateTimeISO('UTC')
    .toPlainDate();
  return windowEndDate.toLocaleString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}
